import { slicedToArray as _slicedToArray } from '../../_virtual/_rollupPluginBabelHelpers.js';
import { ThemeConstellation } from '@zillow/constellation';
import API from '@zillow/mortgage-api-client';
import _debounce from 'lodash/debounce';
import React, { useState, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { setServerUrl, createMax } from '../../utils/mortgagehelpers/mortgage-helpers.js';
import withDimensions from '../PaymentDonutChart/withDimensions.js';
import MortgageRateHistoryTable from './MortgageRateHistoryTable.js';
import { useSyncFetchHistory } from './MortgageRateTrends.utils.js';
import { PROGRAM_COLOR } from './constants.js';

var getSize = function getSize(_ref) {
  var height = _ref.height,
    width = _ref.width,
    containerWidth = _ref.containerWidth;
  var h = Math.round(height);
  var w = Math.round(width);
  if (h && w) {
    return {
      chartHeight: h,
      chartWidth: w
    };
  } else if (h) {
    return {
      chartHeight: h,
      chartWidth: Math.round(containerWidth)
    };
  } else if (w) {
    return {
      chartHeight: Math.round(width / 3 * 2),
      chartWidth: w
    };
  } else {
    return {
      chartHeight: Math.round(containerWidth / 3 * 2),
      chartWidth: Math.round(containerWidth)
    };
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
var ConstellationColors = ThemeConstellation.constellation.colors;
var MortgageRateTrendsChart = function MortgageRateTrendsChart(_ref2) {
  var hideGridLines = _ref2.hideGridLines,
    height = _ref2.height,
    width = _ref2.width,
    containerWidth = _ref2.containerWidth,
    flavor = _ref2.flavor;
  var _useState = useState(function () {
      return getSize({
        height: height,
        width: width,
        containerWidth: containerWidth
      });
    }),
    _useState2 = _slicedToArray(_useState, 2),
    dimensions = _useState2[0],
    setDimensions = _useState2[1];
  useEffect(function () {
    setServerUrl();
    createMax();
  }, []);
  useEffect(function () {
    var updateSize = _debounce(function () {
      setDimensions(getSize({
        height: height,
        width: width,
        containerWidth: containerWidth
      }));
    }, 150);
    updateSize();
    return function () {
      return updateSize.cancel();
    };
  }, [containerWidth, height, width]);
  var form = useFormContext();
  var results = useSyncFetchHistory(form, flavor);

  // rely on MAX to render chart
  var chartUrl = useMemo(function () {
    var chartWidth = dimensions.chartWidth,
      chartHeight = dimensions.chartHeight;
    var url = null;
    var getColorCode = function getColorCode(name) {
      var _ConstellationColors$;
      return (_ConstellationColors$ = ConstellationColors[name]) === null || _ConstellationColors$ === void 0 ? void 0 : _ConstellationColors$.replace('#', '');
    };
    var lines = results === null || results === void 0 ? void 0 : results.checkedLoanProgramFields.map(function (program) {
      var _results$currentField;
      return {
        query: {
          stateAbbreviation: results === null || results === void 0 ? void 0 : results.location,
          program: program,
          loanToValueBucket: results === null || results === void 0 ? void 0 : results.loanToValueBucket,
          creditScoreBucket: results === null || results === void 0 ? void 0 : results.creditScoreBucket,
          refinance: (results === null || results === void 0 || (_results$currentField = results.currentFieldValues) === null || _results$currentField === void 0 ? void 0 : _results$currentField.loanPurpose) == 'Refinance' ? true : false
        },
        color: getColorCode(PROGRAM_COLOR[program])
      };
    });
    if (chartWidth && chartHeight) {
      try {
        var _results$currentField2;
        url = API.MAX.url({
          action: 'rate-chart',
          input: {
            width: Math.max(200, chartWidth),
            height: Math.max(200, chartHeight),
            tickLabelColor: '333333',
            tickLabelFontSize: 8.5,
            xAxisTickCount: 3,
            yAxisTickCount: 4,
            hideGridLines: hideGridLines,
            durationDays: results === null || results === void 0 || (_results$currentField2 = results.currentFieldValues) === null || _results$currentField2 === void 0 ? void 0 : _results$currentField2.timeSpan,
            lines: lines
          }
        });
        return url;
      } catch (e) {
        return null;
      }
    }
  }, [results, dimensions, hideGridLines]);
  var chartStyle = useMemo(function () {
    var chartHeight = dimensions.chartHeight,
      chartWidth = dimensions.chartWidth;
    var h = Math.round(height);
    var w = Math.round(width);
    var results = {
      height: chartHeight + "px",
      backgroundRepeat: 'no-repeat',
      width: '',
      backgroundSize: '',
      backgroundImage: ''
    };

    // Optimize scaling of image for different fixed dimensions
    if (h && w) {
      results.width = chartWidth + "px";
    } else if (h) {
      results.backgroundSize = "100% " + chartHeight + "px";
    } else {
      results.backgroundSize = 'contain';
    }
    if (chartUrl) {
      results.backgroundImage = "url(\"" + chartUrl + "\")";
    }
    return results;
  }, [chartUrl, dimensions, height, width]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    style: chartStyle,
    id: "rates-explorer-chart",
    role: "img",
    "aria-label": "Historical mortgage rates plotted as line graph.",
    "aria-describedby": "rates-explorer-chart-description"
  }), /*#__PURE__*/React.createElement(MortgageRateHistoryTable, {
    id: "rates-explorer-chart-description",
    flavor: flavor
  }));
};
var MortgageRateTrendsChart$1 = withDimensions(MortgageRateTrendsChart, {
  defaultWidth: 980,
  defaultHeight: null
});

export { MortgageRateTrendsChart$1 as default };
